'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-react';
import { motion } from 'framer-motion';
import CarModelCircularProgress from './CircularProgress/CarModelCircularProgress';
import CarModelArrowRight from './ArrowRight/CarModelArrowRight';
import './CarModelInteractiveBooking.scss';
import styles from '../../home/InteractiveBooking/InteractiveBooking.module.scss';

export default function CarModelInteractive({ fields }) {
  // 使用 useMemo 缓存 cards 数组，避免每次渲染都重新计算
  const cards = useMemo(() => {
    return fields.interactive && fields.interactive.length > 0 ? fields.interactive : [];
  }, [fields.interactive]);

  const [activeCard, setActiveCard] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 初始化 activeCard
  useEffect(() => {
    if (cards.length > 0 && !activeCard) {
      setActiveCard(cards[0]);
    }
  }, [cards, activeCard]);

  // 预加载所有图片 - 使用原生 img 标签预加载
  useEffect(() => {
    if (!cards.length) return;

    const preloadImages = () => {
      cards.forEach((card) => {
        const imageUrl = getImageUrl(card.fields?.imageUrl);
        if (imageUrl && !loadedImages.has(imageUrl)) {
          // 创建隐藏的 img 元素进行预加载
          const img = document.createElement('img');
          img.style.display = 'none';
          img.onload = () => {
            setLoadedImages((prev) => new Set([...prev, imageUrl]));
            // 预加载完成后移除，但保留在 loadedImages 状态中
            if (document.body.contains(img)) {
              document.body.removeChild(img);
            }
          };
          img.onerror = () => {
            console.warn('Failed to preload image:', imageUrl);
            setLoadedImages((prev) => new Set([...prev, imageUrl]));
            // 即使失败也要移除
            if (document.body.contains(img)) {
              document.body.removeChild(img);
            }
          };
          img.src = imageUrl;
          document.body.appendChild(img);
        }
      });
    };

    // 延迟预加载，避免阻塞初始渲染
    const timer = setTimeout(preloadImages, 100);
    return () => clearTimeout(timer);
  }, [cards, loadedImages]);

  useEffect(() => {
    if (!isMounted || !cards.length) return;
    let timeout;
    let progressInterval;
    const resetProgress = () => setProgress(0);
    const scheduleNext = () => {
      timeout = setTimeout(() => {
        setActiveCard((prev) => {
          const currentIndex = cards.findIndex((card) => card.id === prev.id);
          const nextIndex = (currentIndex + 1) % cards.length;
          resetProgress();
          return cards[nextIndex];
        });
        scheduleNext();
      }, 5000);
    };

    if (!isHovered) {
      scheduleNext();
      progressInterval = setInterval(() => {
        setProgress((prev) => (prev <= 100 ? prev + 2 : 0));
      }, 100);
    }

    return () => {
      clearTimeout(timeout);
      clearInterval(progressInterval);
    };
  }, [isMounted, isHovered, cards]);

  const handleMouseEnter = (card) => {
    setIsHovered(true);
    setActiveCard(card);
    setProgress(0);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const getImageUrl = (imageData) => {
    // 处理 Sitecore 的嵌套图片数据结构
    if (imageData && imageData.fields) {
      // 优先使用 desktopImage，然后是 tabletImage，最后是 mobileImage
      const desktopImage = imageData.fields.desktopImage?.value?.src;
      const tabletImage = imageData.fields.tabletImage?.value?.src;
      const mobileImage = imageData.fields.mobileImage?.value?.src;

      return desktopImage || tabletImage || mobileImage || '';
    }

    // 处理其他格式
    if (imageData && imageData.value) {
      return imageData.value.href || imageData.value.src || '';
    }

    return imageData?.url || imageData?.src || '';
  };

  const getTitle = (data) => {
    // 处理 Sitecore 的 title 字段
    if (data && data.value) {
      return data.value;
    }
    return data?.title || '';
  };

  const getDescription = (data) => {
    // 处理 Sitecore 的 description 字段
    if (data && data.value) {
      return data.value;
    }
    return data?.description || '';
  };

  const getRedirect = (data) => {
    // 处理 Sitecore 的 redirect 字段
    if (data && data.value && data.value.href) {
      return `${data.value.href || ''}${data.value?.querystring || ''}`;
    }
    if (data && data.value) {
      return data.value.url || '';
    }
    return data?.redirect || '#';
  };

  if (!cards.length) {
    return null;
  }

  return (
    <div className="container car-model-interactive-block">
      <div className={'content'}>
        <ul className={'content__list'}>
          {cards.map((card, index) => {
            const isActive = card.id === activeCard?.id;
            const progressValue = isActive && !isHovered ? progress + 5 : 0;
            const title = getTitle(card.fields?.title);
            const description = getDescription(card.fields?.description);
            const redirect = getRedirect(card.fields?.redirect);

            return (
              <li
                key={card.id}
                className={'content__item'}
                data-aos="fade-up"
                data-aos-delay="500"
                data-aos-duration={(index + 1) * 200}
              >
                <div
                  className={`content__container ${!isActive ? 'inactive' : ''}`}
                  onMouseEnter={() => handleMouseEnter(card)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => {
                    if (redirect && redirect !== '#') {
                      window.open(redirect, '_blank');
                    }
                  }}
                  style={{ cursor: redirect && redirect !== '#' ? 'pointer' : 'default' }}
                >
                  <div>
                    <h4 className={'content__title'}>{title}</h4>
                    <div
                      className={'content__description'}
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                    {/* <p className={'content__description'}>{description}</p> */}
                  </div>
                  <CarModelCircularProgress
                    progress={progressValue}
                    strokeWidth={isActive ? 1 : 0}
                    progressColor="#5e5d60"
                    size={70}
                  >
                    <button
                      className={`content__button ${isActive ? 'content__button_active' : ''}`}
                    >
                      <CarModelArrowRight
                        className={'arrow__icon'}
                        fill={!isActive ? '#121212' : '#FFFFFF'}
                      />
                    </button>
                  </CarModelCircularProgress>
                </div>
              </li>
            );
          })}
        </ul>

        <div className={'content__image_container'}>
          {/* 预加载所有图片，使用绝对定位 */}
          {cards.map((card) => {
            const imageUrl = getImageUrl(card.fields?.imageUrl);
            const isImageLoaded = loadedImages.has(imageUrl);
            const isActive = card.id === activeCard?.id;

            return (
              <div
                key={card.id}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'scale(1)' : 'scale(0.9)',
                  transition: isActive
                    ? 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out'
                    : 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                  zIndex: isActive ? 2 : 1,
                  pointerEvents: isActive ? 'auto' : 'none',
                }}
              >
                {!isImageLoaded && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                )}
                <Image
                  field={{
                    value: {
                      src: imageUrl,
                      alt: getTitle(card.fields?.title),
                    },
                  }}
                  className={'content__image'}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: isImageLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
